"use client";
import { useRef } from "react";
import { usePathname } from "next/navigation";
import { ArrowRight } from "lucide-react";
import { useBannerContext } from "@core/helpers/client/contexts/BannerContext";
import { useMenusContext } from "@core/helpers/client/contexts/MenusContext";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import AuthButtonModal from "@/components/pages/Auth/_ButtonModal";
import SearchBarProduct from "@/components/SearchBarTop/SearchBarProduct";
import CartButton from "@/components/pages/Cart/_CartButton";
import BannerSection, { should_show_banner } from "./_banner";
import { useInitialBanners } from "@/contexts/InitialBanners";
import Logo from "./_logo";
import NavbarShop from "./_navShop";
import NavLoading from "./_loading";
import useScrollDirection from "./useScrollDirection";
import LoadingSpinner from "@/components/LoadingSpinner";
import HeaderBlog from "./_headerBlog";
import HeaderSellerAcademy from "./_headerSellerAcademy";
import is_seller_academy_path from "./_isSellerAcademyPath";
import HeaderAds from "./_headerAds";
import is_ads_path from "./_isAdsPath";
import useSafeBack from "@core/helpers/client/hooks/useSafeBack";
import useIsDesktop from "@core/helpers/client/hooks/useIsDesktop";
import useResizeMeasure from "@/hooks/useResizeMeasure";

export default function HeaderClient({ widget, banner_count = 0 }) {

  const { banner_data, banner_loading } = useBannerContext();
  const show_banner = should_show_banner({ banner_data, banner_loading, initial_count: banner_count, initial_banners: useInitialBanners() });
  const { menus, menus_loading } = useMenusContext()
  // لینک‌های ردیف دوم از سرور می‌آیند (helpers/menuKeys.js)؛ منتظر بقیه‌ی منوها (مثل مگامنو) نمی‌مانیم
  const shop_menu_ready = menus?.["header-main-menu-shop"] !== undefined || !menus_loading;
  const { user_loading } = useAuthContext()

  const widgets = widget?.widgets ?? [];
  const gif_widget = widgets?.find(
    (item) => item?.widget_component_key === "search-gif"
  );

  const goBack = useSafeBack();
  const pathname = usePathname();
  const isBlogPage = pathname.startsWith("/blog");
  const isSingleProduct = pathname.startsWith("/product/");
  const isCheckOut = pathname.startsWith("/checkout/");
  const isCart = pathname.startsWith("/cart");
  const isAccount = pathname.startsWith("/account/");
  const isAmazingPage = pathname === "/landing/amazing-offers/";
  const isContactUs = pathname.startsWith("/contact-us");
  const isAboutUs = pathname.startsWith("/about-us");
  const isSeller = is_seller_academy_path(pathname)

  const hasMobileBackButton = isAmazingPage || isContactUs || isAboutUs;

  const showNavbar = useScrollDirection();
  // SSR و hydration هر دو نسخه را دارند؛ بعد از آن فقط نسخه‌ی همان viewport در DOM می‌ماند
  // (جستجو در هر دو نسخه و مگامنوی دسکتاپ روی موبایل هم mount می‌شدند)
  const is_desktop = useIsDesktop();
  const headerRef = useRef(null);
  const navWrapRef = useRef(null);
  const navInnerRef = useRef(null);

  // ارتفاع هدر و ارتفاعش وقتی ردیف ناوبری باز است، برای فاصله‌ی main و scroll-margin بخش‌ها. فقط در ResizeObserver خوانده
  // می‌شود (useResizeMeasure)؛ اول هر سه اندازه خوانده و بعد هر دو متغیر نوشته می‌شوند.
  useResizeMeasure(
    () => [headerRef.current, navWrapRef.current, navInnerRef.current],
    () => {
      const headerElement = headerRef.current;
      if (!headerElement) return;

      const height = headerElement.getBoundingClientRect().height;
      const collapsed_height = navWrapRef.current?.getBoundingClientRect().height ?? 0;
      const natural_height = navInnerRef.current?.getBoundingClientRect().height ?? collapsed_height;

      return () => {
        const root_style = document.documentElement.style;
        root_style.setProperty("--header-height", `${height}px`);
        root_style.setProperty("--header-height-expanded", `${height - collapsed_height + natural_height}px`);
      };
    },
    [banner_data, showNavbar, shop_menu_ready, is_desktop]
  );

  if (is_ads_path(pathname)) return <HeaderAds />;

  if (isSeller) return <HeaderSellerAcademy banner_count={banner_count} />;

  if (isBlogPage) return <HeaderBlog banner_count={banner_count} />;

  return (
    <header dir="rtl" className="relative">
      <div ref={headerRef} className="fixed top-0 right-0 left-0 z-40 bg-jmt-white">
        {show_banner && <BannerSection initial_count={banner_count} />}

        {/* mobile */}
        {is_desktop !== true && <div className="block lg:hidden">

          {isCart || isCheckOut || isAccount ? null

            : isSingleProduct ? (
              <section className="border-b border-jmt-neutral-200">
                <div className="container">
                  <div className="flex items-center justify-between py-1">
                    <button type="button" className="flex items-center" aria-label="بازگشت"
                      onClick={() => goBack()} >
                      <ArrowRight size={24} />
                    </button>
                    <div className="flex items-center gap-1">
                      <SearchBarProduct widget={gif_widget} />
                      <CartButton />
                    </div>
                  </div>
                </div>
              </section>
            ) : (
              <section className="w-full bg-jmt-white">
                <div className="container">
                  <div className="w-full py-3 flex items-center justify-between gap-4">
                    {hasMobileBackButton && (
                      <button
                        className="flex items-center text-jmt-neutral-900"
                        aria-label="بازگشت"
                        onClick={() => goBack()}
                      >
                        <ArrowRight size={24} />
                      </button>
                    )}

                    <SearchBarProduct widget={gif_widget} />
                  </div>
                </div>
              </section>
            )}
        </div>}

        {/* desktop */}
        {is_desktop !== false && <div className="hidden lg:block">
          <section className="w-full border-b border-jmt-neutral-100 bg-jmt-white">
            <div className="px-[22px]">
              {/* row 1 */}
              <div className="w-full flex items-center justify-between mx-auto space-x-6 py-4">
                <div className="flex items-center justify-start w-full space-x-[38px]">
                  <Logo />
                  <SearchBarProduct widget={gif_widget} />
                </div>

                <div className="flex items-center justify-end gap-6">
                  {user_loading ? <LoadingSpinner /> : <AuthButtonModal />}
                  <CartButton />
                </div>
              </div>

              {/* row 2 */}
              {!shop_menu_ready ? <NavLoading />
                : <div ref={navWrapRef} className={`overflow-hidden transition-all duration-500 ease-in-out ${showNavbar ? "max-h-20 opacity-100" : "max-h-0 opacity-0"
                  }`}>
                  <div ref={navInnerRef}>
                    <NavbarShop />
                  </div>
                </div>}
            </div>
          </section>
        </div>}
      </div>
    </header>
  );
}

